import { EventSearchForm } from '@mark43/cad-api';
import { ResultsTab } from '../search-windows/types';
import { useSearchEventsQuery } from './use-search-events-query';
import { useExecuteSavedEventSearchQuery } from './use-execute-saved-event-search-query';
import { useEventSearchTabsContext } from './event-search-tabs-provider';
import {
    EventSearchResultsPaginationProvider,
    useEventSearchResultsPaginationContext,
} from './event-search-results-pagination-provider';
import { SearchResultsTab } from '../search-windows/search-results-tab';
import { useObservableSearchResultSelectionStore } from '../search-windows/use-observable-search-results-selection-store';
import { EventSearchResultsTable } from './event-search-results-table';
import { observer } from 'mobx-react-lite';
import { EventSearchResultsHeader } from './event-search-results-header';
import { EventSearchSortProvider } from './event-search-sort-provider';
import { useEventSearchFileExport } from './use-event-search-file-export';

const _EventSearchResultsTab = observer(function _EventSearchResultsTab({
    tab,
}: Readonly<{
    tab: ResultsTab<EventSearchForm>;
}>) {
    const tabsValue = useEventSearchTabsContext();
    const pagination = useEventSearchResultsPaginationContext();
    const searchEventsQuery = useSearchEventsQuery(tab.id, tab.search, tab.savedSearchId);
    const executeSavedSearchQuery = useExecuteSavedEventSearchQuery({
        tabId: tab.id,
        savedSearchId: tab.savedSearchId,
        dateRangeOverride: tab.dateRangeOverride,
    });
    const eventSearchResponse = searchEventsQuery.data ?? executeSavedSearchQuery.data;
    const totalCount = eventSearchResponse?.totalCount ?? 0;
    const results = eventSearchResponse?.eventSearchRows.map((row) => ({ id: row.eventId }));
    const selection = useObservableSearchResultSelectionStore(results ?? [], totalCount);

    const { isExportLoading, isCSVExportLoading, isConsolidatedExportAllowed, fileExportHandler } =
        useEventSearchFileExport(tab.search);

    return (
        <SearchResultsTab
            isEventSearch
            tab={tab}
            results={results}
            totalCount={totalCount}
            onClose={() => tabsValue.deleteTab(tab.title)}
            pagination={pagination}
            selection={selection}
            searchQuery={searchEventsQuery}
            executeSavedSearchQuery={executeSavedSearchQuery}
            onFileExport={fileExportHandler}
            isExportLoading={isExportLoading}
            isCSVExportLoading={isCSVExportLoading}
            header={
                <EventSearchResultsHeader
                    hasResults={(results?.length ?? 0) > 0}
                    eventSearchForm={tab.search}
                    savedSearchId={tab.savedSearchId}
                    setSavedSearchId={tab.setSavedSearchId}
                />
            }
            allowConsolidatedExport={isConsolidatedExportAllowed}
        >
            <EventSearchResultsTable
                eventSearchResponse={eventSearchResponse}
                selection={selection}
            />
        </SearchResultsTab>
    );
});

export function EventSearchResultsTab({ tab }: { tab: ResultsTab<EventSearchForm> }) {
    return (
        <EventSearchResultsPaginationProvider>
            <EventSearchSortProvider>
                <_EventSearchResultsTab tab={tab} />
            </EventSearchSortProvider>
        </EventSearchResultsPaginationProvider>
    );
}
